/**
 * EF-EMAIL-ATTACHMENT-INGESTION-V1: Email Attachment Manager
 * 
 * UI component for admins to:
 * - View email attachments from inbox
 * - Assign attachments to crew members as documents
 * - Ignore irrelevant attachments
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Paperclip,
  FileText,
  Image,
  File,
  User,
  Check,
  X,
  Download,
  Loader2,
  RefreshCw,
  Mail,
  Calendar,
  Filter,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { DocumentReviewWorkspaceV2 as DocumentReviewWorkspace } from "./DocumentReviewWorkspaceV2";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge styling
const getStatusBadge = (status) => {
  const styles = {
    new: { variant: "outline", className: "border-blue-500/30 text-blue-400", icon: Clock },
    assigned: { variant: "outline", className: "border-amber-500/30 text-amber-400", icon: User },
    uploaded: { variant: "outline", className: "border-emerald-500/30 text-emerald-400", icon: CheckCircle2 },
    ignored: { variant: "outline", className: "border-zinc-500/30 text-zinc-400", icon: XCircle },
  };
  const style = styles[status] || styles.new;
  const Icon = style.icon;
  
  return (
    <Badge variant={style.variant} className={style.className}>
      <Icon className="h-3 w-3 mr-1" />
      {status}
    </Badge>
  );
};

// File icon based on MIME type
const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith("image/")) return <Image className="h-4 w-4 text-purple-400" />;
  if (mimeType?.includes("pdf")) return <FileText className="h-4 w-4 text-red-400" />;
  return <File className="h-4 w-4 text-zinc-400" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(1)} ${units[i]}`;
};

export function EmailAttachmentManager() {
  // State
  const [attachments, setAttachments] = useState([]);
  const [counts, setCounts] = useState({ new: 0, assigned: 0, uploaded: 0, ignored: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  
  // EF-CREW-DOCUMENT-REVIEW-WORKSPACE-V1: Workspace state
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  
  // Legacy assignment dialog (fallback)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  
  // Assignment form
  const [staffList, setStaffList] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [assignForm, setAssignForm] = useState({
    staff_id: "",
    document_type_id: "",
    issue_date: "",
    expiry_date: "",
    notes: "",
  });

  // Load attachments
  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      
      const res = await fetch(`${API_URL}/api/inbox/attachments?${params}`);
      if (!res.ok) throw new Error("Failed to load attachments");
      
      const data = await res.json();
      setAttachments(data.attachments || []);
      setCounts(data.counts || { new: 0, assigned: 0, uploaded: 0, ignored: 0, total: 0 });
    } catch (err) {
      console.error("Load attachments error:", err);
      toast.error("Failed to load attachments");
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // Load staff list
  const loadStaffList = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff?limit=500`);
      if (!res.ok) throw new Error("Failed to load staff");
      const data = await res.json();
      setStaffList(data.staff || []);
    } catch (err) {
      console.error("Load staff error:", err);
    }
  };

  // Load document types
  const loadDocumentTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/document-types`);
      if (!res.ok) throw new Error("Failed to load document types");
      const data = await res.json();
      setDocumentTypes(data || []);
    } catch (err) {
      console.error("Load document types error:", err);
    }
  };

  // Initial load
  useEffect(() => {
    loadAttachments();
    loadStaffList();
    loadDocumentTypes();
  }, [loadAttachments]);

  // EF-CREW-DOCUMENT-REVIEW-WORKSPACE-V1: Open Document Review Workspace
  const openReviewWorkspace = (attachment) => {
    setSelectedAttachment(attachment);
    setWorkspaceOpen(true);
  };

  // Legacy: Open simple assignment dialog (fallback)
  const openAssignDialog = (attachment) => {
    setSelectedAttachment(attachment);
    setAssignForm({
      staff_id: "",
      document_type_id: "",
      issue_date: "",
      expiry_date: "",
      notes: `Imported from email: ${attachment.email_subject}`,
    });
    setAssignDialogOpen(true);
  };

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedAttachment || !assignForm.staff_id || !assignForm.document_type_id) {
      toast.error("Please select crew member and document type");
      return;
    }

    setAssigning(true);
    try {
      const res = await fetch(`${API_URL}/api/inbox/attachments/${selectedAttachment.id}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(assignForm),
      });

      if (!res.ok) {
        // EF-FIX-CREW-PROFILE-UPDATE-JSON-STREAM-ERROR-V1: Single-read pattern
        let errorData = null;
        try {
          errorData = await res.json();
        } catch {}
        throw new Error(errorData?.detail || "Assignment failed");
      }

      const result = await res.json();
      toast.success(result.message);
      setAssignDialogOpen(false);
      loadAttachments();
    } catch (err) {
      console.error("Assignment error:", err);
      toast.error(err.message || "Failed to assign attachment");
    } finally {
      setAssigning(false);
    }
  };

  // Handle ignore
  const handleIgnore = async (attachment) => {
    try {
      const res = await fetch(`${API_URL}/api/inbox/attachments/${attachment.id}/ignore`, {
        method: "POST",
      });

      if (!res.ok) throw new Error("Failed to ignore attachment");

      toast.success("Attachment marked as ignored");
      loadAttachments();
    } catch (err) {
      console.error("Ignore error:", err);
      toast.error("Failed to ignore attachment");
    }
  };

  // EVENTSFLO-INBOX-ATTACHMENT-DOWNLOAD-FORCE-SAVE-V1:
  // Force save via backend Content-Disposition: attachment. Token query
  // is appended (if available in localStorage) so the auth-protected
  // endpoint accepts the browser-initiated GET.
  const handleDownload = (attachment) => {
    let token = "";
    try { token = localStorage.getItem("eventsflo_auth_token") || ""; } catch { /* noop */ }
    const base = `${API_URL}/api/inbox/attachments/${attachment.id}/file?disposition=attachment`;
    const url = token ? `${base}&token=${encodeURIComponent(token)}` : base;
    const a = document.createElement("a");
    a.href = url;
    a.download = attachment?.original_filename || "";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Email Attachments</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage crew documents from email attachments
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadAttachments}
          disabled={loading}
          data-testid="refresh-attachments-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-5 gap-3">
        <Card className="bg-surface-elevated/30 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">New</p>
                <p className="text-2xl font-bold text-blue-400">{counts.new}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-400/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/30 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Assigned</p>
                <p className="text-2xl font-bold text-amber-400">{counts.assigned}</p>
              </div>
              <User className="h-8 w-8 text-amber-400/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/30 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Uploaded</p>
                <p className="text-2xl font-bold text-emerald-400">{counts.uploaded}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/30 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Ignored</p>
                <p className="text-2xl font-bold text-zinc-400">{counts.ignored}</p>
              </div>
              <XCircle className="h-8 w-8 text-zinc-400/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/30 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total</p>
                <p className="text-2xl font-bold text-foreground">{counts.total}</p>
              </div>
              <Paperclip className="h-8 w-8 text-white/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Attachments</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="uploaded">Uploaded</SelectItem>
            <SelectItem value="ignored">Ignored</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Attachments table */}
      <Card className="bg-surface-elevated/20 border-white/5">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-xs uppercase text-muted-foreground">File</TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground">From</TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground">Email Subject</TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground">Received</TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground">Status</TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading attachments...
                  </TableCell>
                </TableRow>
              ) : attachments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No email attachments found</p>
                    <p className="text-xs mt-1">Attachments from incoming emails will appear here</p>
                  </TableCell>
                </TableRow>
              ) : (
                attachments.map((att) => (
                  <TableRow key={att.id} className="border-white/5" data-testid={`attachment-row-${att.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getFileIcon(att.mime_type)}
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]" title={att.original_filename}>
                            {att.original_filename}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(att.file_size_bytes)}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[150px]" title={att.sender_email}>
                          {att.sender_email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate max-w-[200px] block" title={att.email_subject}>
                        {att.email_subject}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">
                          {new Date(att.received_at).toLocaleDateString()}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(att.status)}
                      {att.assigned_staff_name && (
                        <p className="text-xs text-muted-foreground mt-1">
                          → {att.assigned_staff_name}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(att)}
                          title="Download"
                          data-testid={`download-attachment-${att.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {att.status === "new" && (
                          <>
                            {/* EF-CREW-DOCUMENT-REVIEW-WORKSPACE-V1: Open Review Workspace */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReviewWorkspace(att)}
                              className="text-primary"
                              title="Review and assign document"
                              data-testid={`review-attachment-${att.id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleIgnore(att)}
                              className="text-muted-foreground"
                              title="Mark as ignored"
                              data-testid={`ignore-attachment-${att.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* EF-CREW-DOCUMENT-REVIEW-WORKSPACE-V1: Document Review Workspace */}
      <DocumentReviewWorkspace
        open={workspaceOpen}
        onOpenChange={setWorkspaceOpen}
        attachment={selectedAttachment}
        allAttachments={attachments}
        onAssignComplete={loadAttachments}
      />

      {/* Legacy Assignment Dialog (kept as fallback) */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-primary" />
              Assign Attachment to Crew Member
            </DialogTitle>
          </DialogHeader>

          {selectedAttachment && (
            <div className="space-y-4">
              {/* File preview */}
              <Card className="bg-surface-elevated/30 border-white/5">
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    {getFileIcon(selectedAttachment.mime_type)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{selectedAttachment.original_filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(selectedAttachment.file_size_bytes)} • From: {selectedAttachment.sender_email}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(selectedAttachment)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Crew selection */}
              <div className="space-y-2">
                <Label className="text-sm">
                  Crew Member <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={assignForm.staff_id}
                  onValueChange={(value) => setAssignForm({ ...assignForm, staff_id: value })}
                >
                  <SelectTrigger data-testid="assign-staff-select">
                    <SelectValue placeholder="Select crew member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name}
                        {staff.employee_id && ` (${staff.employee_id})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Document type selection */}
              <div className="space-y-2">
                <Label className="text-sm">
                  Document Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={assignForm.document_type_id}
                  onValueChange={(value) => setAssignForm({ ...assignForm, document_type_id: value })}
                >
                  <SelectTrigger data-testid="assign-doctype-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((docType) => (
                      <SelectItem key={docType.id} value={docType.id}>
                        {docType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Issue Date</Label>
                  <Input
                    type="date"
                    value={assignForm.issue_date}
                    onChange={(e) => setAssignForm({ ...assignForm, issue_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Expiry Date</Label>
                  <Input
                    type="date"
                    value={assignForm.expiry_date}
                    onChange={(e) => setAssignForm({ ...assignForm, expiry_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-sm">Notes</Label>
                <Textarea
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                  placeholder="Optional notes about this document"
                  rows={2}
                />
              </div>

              {/* Info alert */}
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded border border-amber-500/20">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                <p className="text-xs text-amber-200">
                  The document will be created with "pending" status and requires admin verification before it becomes valid.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={assigning || !assignForm.staff_id || !assignForm.document_type_id}
              className="bg-primary"
              data-testid="confirm-assign-btn"
            >
              {assigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Check className="h-4 w-4 mr-1" />
              Assign Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmailAttachmentManager;
